import type { EmrRecord } from '../types/emr';

export const MANISHA_EMR: EmrRecord = {
  version: '1.0.0',
  lastUpdated: '2026-03-01T14:30:00Z',

  patient: {
    id: 'pat-manisha-patel-001',
    mrn: 'BPH-2026-04821',
    firstName: 'Manisha',
    lastName: 'Patel',
    dateOfBirth: '1968-03-15',
    gender: 'Female',
    preferredLanguage: 'en',
    maritalStatus: 'Married',
    ethnicity: 'South Asian',
    race: 'Asian',
    phone: '(203) 555-0147',
    email: 'manisha.patel@email.com',
    address: {
      street: '342 Fairfield Avenue Apt 5B',
      city: 'Bridgeport',
      state: 'CT',
      zip: '06604',
      country: 'US',
    },
    emergencyContacts: [
      {
        name: 'Rajesh Patel',
        relationship: 'Husband',
        phone: '(203) 555-0193',
        isPrimaryContact: true,
      },
      {
        name: 'Priya Patel',
        relationship: 'Daughter',
        phone: '(203) 555-0261',
        isPrimaryContact: false,
      },
    ],
    insurance: {
      provider: 'ConnectiCare',
      planName: 'Choice HMO',
      memberId: 'CCR-9847521',
      groupNumber: 'GRP-BPT-4400',
      effectiveDate: '2025-01-01',
      copayPrimary: 25,
      copaySpecialist: 45,
      copayRx: 15,
    },
    advanceDirectives: {
      hasLivingWill: true,
      hasPowerOfAttorney: true,
      poaName: 'Rajesh Patel',
      poaPhone: '(203) 555-0193',
      codeStatus: 'full-code',
      lastReviewedDate: '2026-02-22',
    },
  },

  diagnoses: [
    {
      id: 'dx-001',
      icd10Code: 'E11.65',
      description: 'Type 2 Diabetes Mellitus with Hyperglycemia',
      category: 'chronic',
      onsetDate: '2019-06-10',
      diagnosedBy: 'Dr. Anand Mehta',
      status: 'active',
      notes: 'Last HbA1c 7.2%, on Metformin 500mg BID. Endocrine consult during hospitalization recommended dose adjustment.',
    },
    {
      id: 'dx-002',
      icd10Code: 'I10',
      description: 'Essential Hypertension',
      category: 'chronic',
      onsetDate: '2020-03-22',
      diagnosedBy: 'Dr. Anand Mehta',
      status: 'active',
      notes: 'Well-controlled on Lisinopril 10mg daily. Target BP < 130/80.',
    },
    {
      id: 'dx-003',
      icd10Code: 'E78.5',
      description: 'Hyperlipidemia, Unspecified',
      category: 'chronic',
      onsetDate: '2021-01-14',
      diagnosedBy: 'Dr. Anand Mehta',
      status: 'active',
      notes: 'LDL 138, target < 100. On Atorvastatin 20mg daily.',
    },
    {
      id: 'dx-004',
      icd10Code: 'K21.0',
      description: 'Gastro-esophageal Reflux Disease with Esophagitis',
      category: 'chronic',
      onsetDate: '2022-08-05',
      diagnosedBy: 'Dr. Lisa Chen',
      status: 'active',
      notes: 'Diagnosed via EGD showing mild esophagitis. On Omeprazole 20mg daily.',
    },
    {
      id: 'dx-005',
      icd10Code: 'E66.01',
      description: 'Morbid Obesity Due to Excess Calories',
      category: 'chronic',
      onsetDate: '2019-06-10',
      diagnosedBy: 'Dr. Anand Mehta',
      status: 'active',
      notes: 'BMI 34.0 at discharge. Weight loss goal of 10 lbs in 3 months. Referred to nutritionist.',
    },
    {
      id: 'dx-006',
      icd10Code: 'N39.0',
      description: 'Urinary Tract Infection, Site Not Specified',
      category: 'acute',
      onsetDate: '2026-02-20',
      diagnosedBy: 'Dr. Maria Santos',
      status: 'resolved',
      notes: 'E. coli UTI treated with IV Ceftriaxone then transitioned to oral Nitrofurantoin. Urine culture cleared on Day 5.',
    },
  ],

  allergies: [
    {
      id: 'alg-001',
      type: 'drug',
      allergen: 'Penicillin',
      reaction: 'Hives and facial swelling',
      severity: 'moderate',
      onsetDate: '1992-04-15',
      verifiedBy: 'Dr. Anand Mehta',
      status: 'active',
    },
    {
      id: 'alg-002',
      type: 'drug',
      allergen: 'Sulfonamides',
      reaction: 'Diffuse maculopapular rash',
      severity: 'mild',
      onsetDate: '2005-09-20',
      verifiedBy: 'Dr. Anand Mehta',
      status: 'active',
    },
    {
      id: 'alg-003',
      type: 'environmental',
      allergen: 'Dust mites',
      reaction: 'Nasal congestion and sneezing',
      severity: 'mild',
      onsetDate: '2010-01-01',
      status: 'active',
    },
  ],

  labResults: [
    {
      id: 'lab-001',
      testName: 'Hemoglobin A1c',
      testCode: '4548-4',
      value: 7.2,
      unit: '%',
      referenceRange: '4.0-5.6%',
      interpretation: 'high',
      collectedDate: '2026-02-22',
      reportedDate: '2026-02-23',
      orderingProvider: 'Dr. Maria Santos',
      labName: 'Bridgepoint Clinical Lab',
      panelName: 'Diabetes Panel',
    },
    {
      id: 'lab-002',
      testName: 'Fasting Glucose',
      testCode: '1558-6',
      value: 142,
      unit: 'mg/dL',
      referenceRange: '70-100 mg/dL',
      interpretation: 'high',
      collectedDate: '2026-02-22',
      reportedDate: '2026-02-22',
      orderingProvider: 'Dr. Maria Santos',
      labName: 'Bridgepoint Clinical Lab',
      panelName: 'Diabetes Panel',
    },
    {
      id: 'lab-003',
      testName: 'Total Cholesterol',
      testCode: '2093-3',
      value: 218,
      unit: 'mg/dL',
      referenceRange: '<200 mg/dL',
      interpretation: 'high',
      collectedDate: '2026-02-22',
      reportedDate: '2026-02-23',
      orderingProvider: 'Dr. Maria Santos',
      labName: 'Bridgepoint Clinical Lab',
      panelName: 'Lipid Panel',
    },
    {
      id: 'lab-004',
      testName: 'LDL Cholesterol',
      testCode: '2089-1',
      value: 138,
      unit: 'mg/dL',
      referenceRange: '<100 mg/dL',
      interpretation: 'high',
      collectedDate: '2026-02-22',
      reportedDate: '2026-02-23',
      orderingProvider: 'Dr. Maria Santos',
      labName: 'Bridgepoint Clinical Lab',
      panelName: 'Lipid Panel',
    },
    {
      id: 'lab-005',
      testName: 'HDL Cholesterol',
      testCode: '2085-9',
      value: 48,
      unit: 'mg/dL',
      referenceRange: '>40 mg/dL',
      interpretation: 'normal',
      collectedDate: '2026-02-22',
      reportedDate: '2026-02-23',
      orderingProvider: 'Dr. Maria Santos',
      labName: 'Bridgepoint Clinical Lab',
      panelName: 'Lipid Panel',
    },
    {
      id: 'lab-006',
      testName: 'Triglycerides',
      testCode: '2571-8',
      value: 160,
      unit: 'mg/dL',
      referenceRange: '<150 mg/dL',
      interpretation: 'high',
      collectedDate: '2026-02-22',
      reportedDate: '2026-02-23',
      orderingProvider: 'Dr. Maria Santos',
      labName: 'Bridgepoint Clinical Lab',
      panelName: 'Lipid Panel',
    },
    {
      id: 'lab-007',
      testName: 'Creatinine',
      testCode: '2160-0',
      value: 0.9,
      unit: 'mg/dL',
      referenceRange: '0.6-1.2 mg/dL',
      interpretation: 'normal',
      collectedDate: '2026-02-22',
      reportedDate: '2026-02-22',
      orderingProvider: 'Dr. Maria Santos',
      labName: 'Bridgepoint Clinical Lab',
      panelName: 'Renal Panel',
    },
    {
      id: 'lab-008',
      testName: 'eGFR',
      testCode: '33914-3',
      value: 82,
      unit: 'mL/min/1.73m2',
      referenceRange: '>60 mL/min/1.73m2',
      interpretation: 'normal',
      collectedDate: '2026-02-22',
      reportedDate: '2026-02-22',
      orderingProvider: 'Dr. Maria Santos',
      labName: 'Bridgepoint Clinical Lab',
      panelName: 'Renal Panel',
    },
    {
      id: 'lab-009',
      testName: 'Potassium',
      testCode: '2823-3',
      value: 4.3,
      unit: 'mEq/L',
      referenceRange: '3.5-5.0 mEq/L',
      interpretation: 'normal',
      collectedDate: '2026-02-22',
      reportedDate: '2026-02-22',
      orderingProvider: 'Dr. Maria Santos',
      labName: 'Bridgepoint Clinical Lab',
      panelName: 'Basic Metabolic Panel',
    },
    {
      id: 'lab-010',
      testName: 'ALT (Alanine Aminotransferase)',
      testCode: '1742-6',
      value: 28,
      unit: 'U/L',
      referenceRange: '7-56 U/L',
      interpretation: 'normal',
      collectedDate: '2026-02-22',
      reportedDate: '2026-02-22',
      orderingProvider: 'Dr. Maria Santos',
      labName: 'Bridgepoint Clinical Lab',
      panelName: 'Hepatic Panel',
    },
    {
      id: 'lab-011',
      testName: 'AST (Aspartate Aminotransferase)',
      testCode: '1920-8',
      value: 24,
      unit: 'U/L',
      referenceRange: '10-40 U/L',
      interpretation: 'normal',
      collectedDate: '2026-02-22',
      reportedDate: '2026-02-22',
      orderingProvider: 'Dr. Maria Santos',
      labName: 'Bridgepoint Clinical Lab',
      panelName: 'Hepatic Panel',
    },
    {
      id: 'lab-012',
      testName: 'TSH (Thyroid Stimulating Hormone)',
      testCode: '3016-3',
      value: 2.8,
      unit: 'mIU/L',
      referenceRange: '0.4-4.0 mIU/L',
      interpretation: 'normal',
      collectedDate: '2026-02-22',
      reportedDate: '2026-02-23',
      orderingProvider: 'Dr. Maria Santos',
      labName: 'Bridgepoint Clinical Lab',
      panelName: 'Thyroid Panel',
    },
    {
      id: 'lab-013',
      testName: 'Urine Microalbumin/Creatinine Ratio',
      testCode: '14959-1',
      value: 22,
      unit: 'mcg/mg',
      referenceRange: '<30 mcg/mg',
      interpretation: 'normal',
      collectedDate: '2026-02-22',
      reportedDate: '2026-02-23',
      orderingProvider: 'Dr. Maria Santos',
      labName: 'Bridgepoint Clinical Lab',
      panelName: 'Diabetes Panel',
    },
  ],

  vitalSigns: [
    {
      id: 'vs-001',
      date: '2026-02-22',
      systolicBP: 152,
      diastolicBP: 96,
      heartRate: 92,
      temperature: 101.2,
      respiratoryRate: 20,
      oxygenSaturation: 94,
      weight: 201,
      height: 65,
      painLevel: 5,
      recordedBy: 'RN Karen Whitfield',
      location: 'Emergency Department',
    },
    {
      id: 'vs-002',
      date: '2026-02-25',
      systolicBP: 144,
      diastolicBP: 92,
      heartRate: 88,
      temperature: 100.1,
      respiratoryRate: 18,
      oxygenSaturation: 95,
      weight: 200,
      painLevel: 4,
      recordedBy: 'RN Deborah Owens',
      location: 'Medical Floor 3B',
    },
    {
      id: 'vs-003',
      date: '2026-02-27',
      systolicBP: 132,
      diastolicBP: 84,
      heartRate: 80,
      temperature: 98.6,
      respiratoryRate: 16,
      oxygenSaturation: 96,
      weight: 198,
      painLevel: 2,
      recordedBy: 'RN Deborah Owens',
      location: 'Medical Floor 3B',
    },
    {
      id: 'vs-004',
      date: '2026-02-28',
      systolicBP: 126,
      diastolicBP: 78,
      heartRate: 76,
      temperature: 98.4,
      respiratoryRate: 16,
      oxygenSaturation: 97,
      weight: 198,
      height: 65,
      bmi: 34.0,
      painLevel: 1,
      recordedBy: 'RN Karen Whitfield',
      location: 'Medical Floor 3B',
    },
  ],

  procedures: [
    {
      id: 'proc-001',
      cptCode: '43239',
      description: 'Esophagogastroduodenoscopy (EGD) with Biopsy',
      date: '2022-08-05',
      performer: 'Dr. Lisa Chen',
      facility: 'Bridgepoint Healthcare Endoscopy Suite',
      indication: 'Persistent heartburn unresponsive to lifestyle modifications, evaluate for Barrett esophagus',
      outcome: 'Mild distal esophagitis (LA Grade A). No Barrett esophagus. Gastric mucosa normal. Biopsy negative for H. pylori.',
      notes: 'Patient tolerated procedure well under moderate sedation. Omeprazole 20mg daily initiated.',
    },
    {
      id: 'proc-002',
      cptCode: '92250',
      description: 'Diabetic Retinal Examination with Fundus Photography',
      date: '2025-09-12',
      performer: 'Dr. James Park',
      facility: 'Bridgeport Eye Associates',
      indication: 'Annual diabetic retinopathy screening',
      outcome: 'No diabetic retinopathy. Mild nuclear sclerotic cataracts bilaterally, not visually significant at this time.',
      notes: 'Follow-up in 12 months. Cataract monitoring recommended.',
    },
  ],

  hospitalizations: [
    {
      id: 'hosp-001',
      facility: 'Bridgepoint Healthcare',
      admissionDate: '2026-02-22',
      dischargeDate: '2026-03-01',
      admittingDiagnosis: 'Urinary tract infection with sepsis workup, hyperglycemic episode',
      dischargeDiagnosis: 'E. coli urinary tract infection with concurrent hyperglycemia in setting of Type 2 Diabetes',
      attendingPhysician: 'Dr. Maria Santos',
      type: 'inpatient',
      dischargeDisposition: 'Home with self-care',
    },
  ],

  dischargeSummary: {
    hospitalName: 'Bridgepoint Healthcare',
    admissionDate: '2026-02-22',
    dischargeDate: '2026-03-01',
    attendingPhysician: 'Dr. Maria Santos',
    admittingDiagnosis: 'Urinary tract infection with sepsis workup, hyperglycemic episode',
    hospitalCourse:
      'Ms. Patel is a 57-year-old woman with a history of Type 2 Diabetes, Hypertension, Hyperlipidemia, GERD, and Morbid Obesity who presented to the Emergency Department on 2/22/2026 with dysuria, urinary frequency, suprapubic pain, and fever of 101.2F for two days. ' +
      'Initial workup revealed a urinalysis positive for leukocyte esterase, nitrites, and >100 WBC/hpf. Urine culture subsequently grew E. coli >100,000 CFU/mL sensitive to Ceftriaxone and Nitrofurantoin, resistant to Ampicillin and TMP-SMX. Blood cultures were drawn given fever and were negative at 48 hours. ' +
      'Admission labs were notable for blood glucose of 342 mg/dL with an HbA1c of 7.2%, indicating suboptimal but not acutely decompensated glycemic control. The hyperglycemia was attributed to the infectious stress response. ' +
      'Treatment: The patient was started on IV Ceftriaxone 1g daily for the UTI. Metformin was held for 24 hours due to the acute illness and risk of lactic acidosis, and an insulin sliding scale was used for glycemic management during this period. ' +
      'By hospital Day 3 (2/25), the patient was afebrile with improving symptoms. Metformin was restarted at the home dose of 500mg twice daily. Antibiotics were transitioned from IV Ceftriaxone to oral Nitrofurantoin 100mg twice daily on Day 5 (2/27) to complete a total 7-day antibiotic course. ' +
      'Endocrinology was consulted on Day 4 and recommended continuing current Metformin dosing with close outpatient follow-up given the HbA1c of 7.2%, with consideration for dose increase or addition of a second agent if HbA1c does not improve to below 7.0% within 3 months. ' +
      'Blood pressure was elevated on admission (152/96) likely related to pain and infection. Home Lisinopril was continued and BP trended down to 126/78 by discharge. Atorvastatin and Omeprazole were continued at home doses throughout the hospitalization. ' +
      'The patient was seen by physical therapy for deconditioning assessment and cleared for discharge with independent ambulation. Nutritional counseling was provided regarding a diabetic-friendly, heart-healthy, low-sodium diet. ' +
      'At discharge, the patient was afebrile for >72 hours, tolerating oral medications and diet, voiding without difficulty, and blood glucose was stable between 120-160 mg/dL on oral Metformin alone.',
    dischargeDiagnoses: [
      'E. coli urinary tract infection, treated',
      'Type 2 Diabetes Mellitus with hyperglycemia, improved',
      'Essential Hypertension, controlled',
      'Hyperlipidemia',
      'Gastro-esophageal reflux disease with esophagitis',
      'Morbid obesity',
    ],
    dischargeCondition: 'Stable, improved. Ambulating independently. Tolerating oral diet and medications.',
    dischargeMedications: [
      {
        name: 'Metformin',
        dose: '500mg',
        frequency: 'Twice daily',
        route: 'Oral',
        instructions: 'Take with breakfast and dinner. Do not skip meals while taking this medication.',
        isNew: false,
        isChanged: false,
      },
      {
        name: 'Lisinopril',
        dose: '10mg',
        frequency: 'Once daily',
        route: 'Oral',
        instructions: 'Take in the morning. Rise slowly from sitting or lying position to avoid dizziness.',
        isNew: false,
        isChanged: false,
      },
      {
        name: 'Atorvastatin',
        dose: '20mg',
        frequency: 'Once daily',
        route: 'Oral',
        instructions: 'Take at bedtime. Report any unexplained muscle pain or weakness.',
        isNew: false,
        isChanged: false,
      },
      {
        name: 'Omeprazole',
        dose: '20mg',
        frequency: 'Once daily',
        route: 'Oral',
        instructions: 'Take 30 minutes before breakfast on an empty stomach.',
        isNew: false,
        isChanged: false,
      },
      {
        name: 'Nitrofurantoin',
        dose: '100mg',
        frequency: 'Twice daily',
        route: 'Oral',
        instructions: 'Take with food to reduce stomach upset. Complete the full 7-day course even if you feel better. Last dose on March 5, 2026.',
        isNew: true,
        isChanged: false,
      },
    ],
    followUpAppointments: [
      {
        provider: 'Dr. Anand Mehta',
        specialty: 'Primary Care',
        date: '2026-03-08',
        reason: 'Post-hospitalization follow-up, review labs, assess UTI resolution, diabetes and blood pressure management',
        phone: '(203) 555-0300',
        address: '1275 Main Street Suite 200, Bridgeport, CT 06604',
        confirmed: true,
      },
      {
        provider: 'Dr. Rachel Kim',
        specialty: 'Endocrinology',
        date: '2026-03-22',
        reason: 'Diabetes management review, evaluate need for medication adjustment based on HbA1c trend',
        phone: '(203) 555-0415',
        address: '900 Madison Avenue Suite 310, Bridgeport, CT 06606',
        confirmed: true,
      },
      {
        provider: 'Sarah Nguyen, RD',
        specialty: 'Nutrition / Dietetics',
        date: '2026-03-15',
        reason: 'Nutritional counseling for diabetes-friendly, heart-healthy, calorie-controlled diet; weight loss plan',
        phone: '(203) 555-0388',
        address: '1275 Main Street Suite 105, Bridgeport, CT 06604',
        confirmed: false,
      },
    ],
    dischargeInstructions:
      'Complete the full course of Nitrofurantoin as prescribed (last dose March 5, 2026). ' +
      'Drink at least 8 glasses of water daily to support urinary health. ' +
      'Monitor blood sugar at home twice daily (fasting morning and before dinner) and keep a log for your PCP and endocrinologist. ' +
      'Follow a low-sodium, low-sugar, heart-healthy diet as discussed with the nutritional counselor. ' +
      'Weigh yourself every morning before eating and record it. Contact your doctor if you gain more than 3 lbs in one day or 5 lbs in one week. ' +
      'Take all medications as prescribed at the same times each day. Do not stop any medication without consulting your doctor. ' +
      'Resume normal activity as tolerated. Walk at least 15-20 minutes daily, increasing gradually.',
    activityRestrictions: [
      'No heavy lifting over 20 lbs for one week',
      'Resume normal activity as tolerated',
      'Begin walking 15-20 minutes daily and increase gradually',
      'Avoid prolonged bed rest',
    ],
    dietaryInstructions:
      'Follow a diabetic-friendly, heart-healthy diet: limit sodium to 1,500mg/day, limit added sugars, eat plenty of vegetables and lean proteins, choose whole grains over refined carbs. ' +
      'Eat at regular intervals and do not skip meals, especially while taking Metformin. ' +
      'Increase fluid intake to at least 8 glasses of water daily. Limit caffeine and avoid alcohol while on Nitrofurantoin.',
    warningSignsToWatch: [
      'Fever above 100.4F (38C)',
      'Return of burning or pain with urination',
      'Blood in urine or cloudy, foul-smelling urine',
      'Blood sugar consistently above 250 mg/dL or below 70 mg/dL',
      'Severe dizziness, fainting, or persistent headache',
      'Chest pain, shortness of breath, or sudden leg swelling',
      'Unexplained muscle pain or weakness (while on Atorvastatin)',
      'Persistent nausea, vomiting, or inability to keep medications down',
    ],
  },

  carePlan: {
    goals: [
      {
        id: 'goal-001',
        description: 'Reduce HbA1c to below 7.0%',
        targetDate: '2026-06-01',
        status: 'active',
        category: 'Diabetes Management',
        metric: 'HbA1c',
        targetValue: '<7.0%',
        currentValue: '7.2%',
      },
      {
        id: 'goal-002',
        description: 'Achieve blood pressure consistently below 130/80 mmHg',
        targetDate: '2026-06-01',
        status: 'active',
        category: 'Cardiovascular Health',
        metric: 'Blood Pressure',
        targetValue: '<130/80 mmHg',
        currentValue: '126/78 mmHg',
      },
      {
        id: 'goal-003',
        description: 'Reduce LDL cholesterol to below 100 mg/dL',
        targetDate: '2026-09-01',
        status: 'active',
        category: 'Cardiovascular Health',
        metric: 'LDL Cholesterol',
        targetValue: '<100 mg/dL',
        currentValue: '138 mg/dL',
      },
      {
        id: 'goal-004',
        description: 'Lose 10 pounds through diet modification and increased physical activity',
        targetDate: '2026-06-01',
        status: 'active',
        category: 'Weight Management',
        metric: 'Weight',
        targetValue: '188 lbs',
        currentValue: '198 lbs',
      },
      {
        id: 'goal-005',
        description: 'Complete full antibiotic course for UTI (Nitrofurantoin through March 5, 2026)',
        targetDate: '2026-03-05',
        status: 'active',
        category: 'Infection Resolution',
        metric: 'Antibiotic Completion',
        targetValue: '14 doses (7 days x 2/day)',
        currentValue: '0 doses taken post-discharge',
      },
    ],
    followUps: [
      {
        provider: 'Dr. Anand Mehta',
        specialty: 'Primary Care',
        date: '2026-03-08',
        reason: 'Post-hospitalization follow-up, review labs, assess UTI resolution',
        phone: '(203) 555-0300',
        address: '1275 Main Street Suite 200, Bridgeport, CT 06604',
        confirmed: true,
      },
      {
        provider: 'Dr. Rachel Kim',
        specialty: 'Endocrinology',
        date: '2026-03-22',
        reason: 'Diabetes management review and medication adjustment evaluation',
        phone: '(203) 555-0415',
        address: '900 Madison Avenue Suite 310, Bridgeport, CT 06606',
        confirmed: true,
      },
      {
        provider: 'Sarah Nguyen, RD',
        specialty: 'Nutrition / Dietetics',
        date: '2026-03-15',
        reason: 'Nutritional counseling for weight loss and diabetes-friendly diet',
        phone: '(203) 555-0388',
        address: '1275 Main Street Suite 105, Bridgeport, CT 06604',
        confirmed: false,
      },
      {
        provider: 'Dr. James Park',
        specialty: 'Ophthalmology',
        date: '2026-09-12',
        reason: 'Annual diabetic retinal exam and cataract monitoring',
        phone: '(203) 555-0472',
        address: '450 East Main Street, Bridgeport, CT 06608',
        confirmed: false,
      },
    ],
    selfCareInstructions: [
      'Check blood sugar twice daily (fasting in the morning and before dinner) and record readings in a log',
      'Take all medications at the same time each day as prescribed; do not skip doses or stop medications without consulting your doctor',
      'Drink at least 8 glasses of water per day; increase fluid intake if urine appears dark or concentrated',
      'Weigh yourself every morning before eating, wearing similar clothing; record the number and report sudden gains',
      'Walk at least 15-20 minutes every day, gradually increasing to 30 minutes as tolerated',
      'Inspect feet daily for cuts, blisters, redness, or swelling; report any wounds that do not heal',
    ],
    educationTopics: [
      'Understanding Type 2 Diabetes: blood sugar targets, how medications work, recognizing highs and lows',
      'Blood pressure management: sodium restriction, medication adherence, home monitoring technique',
      'Heart-healthy eating: choosing whole grains, lean proteins, limiting saturated fats and added sugars',
      'Recognizing urinary tract infection symptoms and when to seek immediate medical attention',
      'Medication safety: proper storage, what to do if a dose is missed, avoiding drug interactions',
      'When to call 911: chest pain, sudden weakness or numbness, difficulty breathing, blood sugar below 54 mg/dL',
    ],
    referrals: [
      {
        specialty: 'Endocrinology',
        provider: 'Dr. Rachel Kim',
        reason: 'Diabetes medication optimization; HbA1c 7.2% on Metformin monotherapy, assess need for second agent',
        priority: 'routine',
        status: 'scheduled',
        scheduledDate: '2026-03-22',
      },
      {
        specialty: 'Nutrition / Dietetics',
        provider: 'Sarah Nguyen, RD',
        reason: 'Medical nutrition therapy for diabetes, hypertension, hyperlipidemia, and weight management',
        priority: 'routine',
        status: 'scheduled',
        scheduledDate: '2026-03-15',
      },
      {
        specialty: 'Diabetes Self-Management Education (DSME)',
        reason: 'Comprehensive diabetes education program including glucose monitoring, medication management, and lifestyle modification',
        priority: 'routine',
        status: 'pending',
      },
    ],
  },

  immunizations: [
    {
      id: 'imm-001',
      vaccineName: 'Influenza (Quadrivalent, High-Dose)',
      cvxCode: '197',
      dateAdministered: '2025-10-15',
      site: 'Left deltoid',
      lotNumber: 'FL-2025-8834',
      manufacturer: 'Sanofi Pasteur',
      administeredBy: 'RN Patricia Long',
      facility: 'Bridgepoint Healthcare Primary Care',
    },
    {
      id: 'imm-002',
      vaccineName: 'COVID-19 mRNA (Updated 2025-2026)',
      cvxCode: '313',
      dateAdministered: '2025-10-15',
      site: 'Right deltoid',
      lotNumber: 'CV-2025-XR42',
      manufacturer: 'Moderna',
      administeredBy: 'RN Patricia Long',
      facility: 'Bridgepoint Healthcare Primary Care',
    },
    {
      id: 'imm-003',
      vaccineName: 'Tdap (Tetanus, Diphtheria, Pertussis)',
      cvxCode: '115',
      dateAdministered: '2023-05-20',
      site: 'Left deltoid',
      lotNumber: 'TD-2023-1156',
      manufacturer: 'GlaxoSmithKline',
      administeredBy: 'RN Carol Edwards',
      facility: 'Bridgepoint Healthcare Primary Care',
    },
    {
      id: 'imm-004',
      vaccineName: 'Pneumococcal Conjugate (PCV20)',
      cvxCode: '216',
      dateAdministered: '2024-03-08',
      site: 'Left deltoid',
      lotNumber: 'PN-2024-3301',
      manufacturer: 'Pfizer',
      administeredBy: 'RN Patricia Long',
      facility: 'Bridgepoint Healthcare Primary Care',
    },
    {
      id: 'imm-005',
      vaccineName: 'Shingles (Recombinant, Adjuvanted)',
      cvxCode: '187',
      dateAdministered: '2024-03-08',
      site: 'Right deltoid',
      lotNumber: 'SH-2024-0092',
      manufacturer: 'GlaxoSmithKline',
      administeredBy: 'RN Patricia Long',
      facility: 'Bridgepoint Healthcare Primary Care',
      doseNumber: 2,
    },
    {
      id: 'imm-006',
      vaccineName: 'Hepatitis B (Recombinant)',
      cvxCode: '189',
      dateAdministered: '2022-07-14',
      site: 'Left deltoid',
      lotNumber: 'HB-2022-5510',
      manufacturer: 'Dynavax',
      administeredBy: 'RN Carol Edwards',
      facility: 'Bridgepoint Healthcare Primary Care',
      doseNumber: 3,
    },
  ],
};
